import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DeliveryAddressSelection, formatAddressLabel } from '@steelbuy/form';
import {
    AddressModel,
    MaterialProperties,
    MaterialWithDimensions,
    Literal,
    getDimensionUnit,
    getTradeUnit,
    Product,
    ThicknessOption,
    ThicknessType,
    WidthOption,
} from '@steelbuy/ts-shared';
import {
    ButtonColor,
    ButtonDanger,
    ButtonGhostSystemOnLightS,
    ButtonSize,
    ButtonTertiary,
    Card,
    Notification,
    NotificationLevel,
    Tooltip,
    TooltipType,
} from '@steelbuy/ui-primitive';
import { entries } from '@steelbuy/util';
import {
    RFQInputs,
    isUniqueEntry,
    removeNullFields,
    updateNonApplicableFields,
    mapInputs,
    mapStateForInput,
} from '../../createRFQUtil';
import { useRfqFormContext } from '../../RfqFormContext';

import '../../../rfqs/RFQCommonStyles.scss';
import './RFQTable.scss';

type RowProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: any;
    material: MaterialProperties;
    headings: RFQInputs;
    data?: MaterialWithDimensions;
    isEdit: boolean;
    onDelete?: (index: number) => void;
    index?: number;
    errors?: FieldErrors<MaterialWithDimensions>;
    onEdit?: (index: number) => void;
    onAddSimilar?: () => void;
    onEditConfirm?: () => void;
    onEditCancel?: () => void;
    isAddSimilar?: boolean;
    notificationError?: string;
    isValid?: boolean;
    warehouseAddresses?: AddressModel[];
    deliveryAddressRequired?: boolean;
};

export const Row = ({
    control,
    data,
    headings,
    index,
    onDelete,
    isEdit,
    material: rowMaterial,
    errors,
    onEdit,
    onAddSimilar,
    onEditConfirm,
    onEditCancel,
    isAddSimilar,
    notificationError,
    isValid,
    warehouseAddresses = [],
    deliveryAddressRequired = false,
}: RowProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const showAddressSelector = warehouseAddresses.length > 0;
    const formattedAddress = data?.deliveryAddress
        ? formatAddressLabel(
              warehouseAddresses.find((a) => a.id === data.deliveryAddress),
              t as TFunction
          )
        : '';
    return (
        <>
            <tr className={classNames('rfq-row', { 'rfq-row--error': !!notificationError })}>
                {entries(headings).map(
                    ([
                        key,
                        {
                            Component,
                            literalPath,
                            rules,
                            rulesMin,
                            rulesMax,
                            valueMapper,
                            getOptions,
                            disabledFn,
                            translationKey,
                            endAdornment,
                        },
                    ]) => {
                        let value: string | number | undefined = '';

                        if (data) {
                            if (valueMapper) {
                                // valueMapper handles all display logic including range formatting,
                                // so call it whenever data is present regardless of data[key]
                                value = valueMapper(data);
                            } else if (data[key]) {
                                if (literalPath) {
                                    value = t(`domainModel:${String(literalPath)}.${data[key]}` as Literal);
                                } else if (translationKey) {
                                    value = t(`domainModel:${translationKey}.${data[key]}` as Literal);
                                } else {
                                    value = data[key];
                                }
                            }
                        }

                        return (
                            <td key={key}>
                                {isEdit || isAddSimilar ? (
                                    <Component
                                        material={rowMaterial}
                                        control={control}
                                        name={key}
                                        rules={rules}
                                        rulesMin={rulesMin}
                                        rulesMax={rulesMax}
                                        error={errors?.[key]}
                                        errors={errors}
                                        getOptions={getOptions}
                                        translationKey={translationKey}
                                        endAdornment={endAdornment}
                                        disabled={disabledFn?.(rowMaterial)}
                                    />
                                ) : (
                                    <span className={classNames({ 'rfq-row__not-applicable': value === '' })}>
                                        {value === ''
                                            ? t('application.createBulkListing.editListings.notApplicable')
                                            : value}
                                    </span>
                                )}
                            </td>
                        );
                    }
                )}
                {showAddressSelector &&
                    (isEdit || isAddSimilar ? (
                        <td className="rfq-row__delivery-address-cell">
                            <DeliveryAddressSelection
                                name="deliveryAddress"
                                control={control}
                                addresses={warehouseAddresses}
                                error={errors?.deliveryAddress}
                                rules={deliveryAddressRequired ? { required: true } : undefined}
                            />
                        </td>
                    ) : (
                        <Tooltip content={formattedAddress} type={TooltipType.INFO}>
                            <td className="rfq-row__delivery-address-cell">{formattedAddress}</td>
                        </Tooltip>
                    ))}
                {!isEdit && onDelete && index !== undefined && (
                    <td className="rfq-row__buttons-cell">
                        <div className="rfq-row__buttons-cell__container">
                            <ButtonGhostSystemOnLightS
                                label={t('uiDomain:common.edit')}
                                onClick={() => onEdit?.(index)}
                            />
                            <ButtonTertiary
                                label={t('translation:application.createRFQ.addSimilar')}
                                size={ButtonSize.S}
                                onClick={onAddSimilar}
                            />
                            <ButtonDanger
                                label={t('uiDomain:common.delete')}
                                size={ButtonSize.S}
                                onClick={() => onDelete(index)}
                            />
                        </div>
                    </td>
                )}
                {((isEdit && onDelete && index !== undefined) || isAddSimilar) && (
                    <td className="rfq-row__buttons-cell">
                        <div
                            className={classNames(
                                'rfq-row__buttons-cell__container',
                                'rfq-row__buttons-cell__container--edit'
                            )}
                        >
                            <ButtonTertiary
                                label={t('uiDomain:common.cancel')}
                                size={ButtonSize.S}
                                onClick={onEditCancel}
                            />
                            <ButtonTertiary
                                size={ButtonSize.S}
                                color={ButtonColor.DARK}
                                label={t('uiDomain:common.confirm')}
                                onClick={onEditConfirm}
                                disabled={!isValid}
                            />
                        </div>
                    </td>
                )}
            </tr>
            {notificationError && (
                <tr className="rfq-row rfq-row-notification">
                    <td colSpan={100} className="rfq-row-notification__cell">
                        <Notification
                            level={NotificationLevel.ERROR}
                            message={notificationError}
                            stayOpen
                            disableScroll
                        />
                    </td>
                </tr>
            )}
        </>
    );
};

interface RFQTableProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fields: any[];
    material: MaterialWithDimensions;
    headings: RFQInputs;
    onDelete: (rowIndex: number) => void;
    groupIndex: number;
    append: (data: MaterialWithDimensions) => void;
    update: (rowIndex: number, formValues: MaterialWithDimensions) => void;
    onEditDirtyChange?: (isDirty: boolean) => void;
}

const addSimilarDefaultValues = {
    width: '',
    widthMin: '',
    widthMax: '',
    thickness: '',
    thicknessMin: '',
    thicknessMax: '',
    weight: '',
};

export const RFQTable = ({
    fields,
    headings,
    material,
    groupIndex,
    append,
    update,
    onDelete,
    onEditDirtyChange,
}: RFQTableProps) => {
    const methods = useForm({
        mode: 'all',
    });

    const {
        control,
        getValues,
        formState: { errors, isValid, dirtyFields },
        watch,
        reset,
        clearErrors,
        setValue,
    } = methods;

    const { t } = useTranslation('translation');

    const { editRowByGroup, editRow, cancelEditRow, addSimilarRow, addSimilarGroupIndex, warehouseAddresses } =
        useRfqFormContext();
    const editRowForGroup = editRowByGroup?.[groupIndex];
    const [error, setError] = useState('');
    const showAddressSelector = warehouseAddresses.length > 0;

    useEffect(() => {
        updateNonApplicableFields(headings, { ...material, ...watch() }, clearErrors, watch, setValue, errors);
    }, [watch()]);

    // Clear opposing thickness fields when the user switches mode during an edit
    const thicknessOptionValue = watch('thicknessOption');
    const widthOptionValue = watch('widthOption');
    useEffect(() => {
        if (thicknessOptionValue === ThicknessOption.RANGE) {
            setValue('thickness', '');
            clearErrors('thickness');
        } else if (thicknessOptionValue === ThicknessOption.SINGLE) {
            setValue('thicknessMin', '');
            setValue('thicknessMax', '');
            clearErrors('thicknessMin');
            clearErrors('thicknessMax');
            if (material.product === Product.SHEET) {
                setValue('thicknessType', ThicknessType.NOM);
            }
        }
    }, [thicknessOptionValue]);

    useEffect(() => {
        if (widthOptionValue === WidthOption.RANGE) {
            setValue('width', '');
            clearErrors('width');
        } else if (widthOptionValue === WidthOption.SINGLE) {
            setValue('widthMin', '');
            setValue('widthMax', '');
            clearErrors('widthMin');
            clearErrors('widthMax');
        }
    }, [widthOptionValue]);
    const hasDirtyFields = Object.keys(dirtyFields).length > 0;
    useEffect(() => {
        onEditDirtyChange?.(hasDirtyFields);
    }, [hasDirtyFields]);
    if (fields.length === 0) {
        return null;
    }

    return (
        <section className="rfq-table">
            <Card isClickable={false}>
                <div className="rfq-table-container">
                    <table className="rfq-table__table">
                        <thead>
                            <tr className="rfq-table__label">
                                {Object.entries(headings).map(([key, { measurementLiteral, measurementType }]) => {
                                    let measurementText = '';
                                    if (measurementType !== undefined) {
                                        const literal =
                                            measurementType === 'dimension'
                                                ? `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                : (`domainModel:material.tradeUnit.value.${getTradeUnit({
                                                      materialType: material.materialType,
                                                      product: material.product,
                                                  })}.label` as Literal);
                                        measurementText = ` (${t(literal)})`;
                                    } else if (measurementLiteral) {
                                        const literalText = t(`domainModel:${measurementLiteral}` as Literal);
                                        measurementText = ` (${literalText})`;
                                    }
                                    return (
                                        <th key={key}>
                                            {t(`application.rfqHeadingLabels.${key}` as Literal)}
                                            {measurementText ? <span>{measurementText}</span> : null}
                                        </th>
                                    );
                                })}
                                {showAddressSelector && (
                                    <th>{t('application.rfqHeadingLabels.deliveryAddress' as Literal)}</th>
                                )}
                                <th>
                                    <div>
                                        <span className="rfq-table__table__actions">
                                            {t('application.createRFQ.actions')}
                                        </span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <FormProvider {...methods}>
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                {fields.map((field: any, index: number) => (
                                    <Row
                                        data={field}
                                        headings={headings}
                                        material={{ ...material, ...watch() }}
                                        control={control}
                                        isEdit={editRowForGroup === index}
                                        key={field.id}
                                        index={index}
                                        onDelete={(removeIndex: number) => {
                                            onDelete(removeIndex);
                                            if (editRowForGroup > removeIndex) {
                                                editRow(groupIndex, editRowForGroup - 1);
                                            }
                                        }}
                                        onEdit={(rowIndex) => {
                                            setError('');
                                            reset(mapStateForInput(field));
                                            editRow(groupIndex, rowIndex);
                                        }}
                                        onAddSimilar={() => {
                                            setError('');
                                            const defaultValues = {
                                                ...field,
                                                ...addSimilarDefaultValues,
                                                ...(field.length !== undefined ? { length: '' } : {}),
                                            };
                                            reset(mapStateForInput(defaultValues));
                                            addSimilarRow(groupIndex);
                                        }}
                                        onEditConfirm={() => {
                                            const isUnique = isUniqueEntry(getValues(), fields, index);
                                            if (isUnique) {
                                                setError('');
                                                let mappedValues = removeNullFields(getValues());
                                                mappedValues = mapInputs(mappedValues);
                                                update(index, mappedValues);
                                                cancelEditRow();
                                            } else {
                                                setError(t('application.addRfq.itemExists'));
                                            }
                                        }}
                                        onEditCancel={() => {
                                            setError('');
                                            cancelEditRow();
                                        }}
                                        errors={errors}
                                        notificationError={editRowForGroup === index ? error : ''}
                                        isValid={isValid}
                                        warehouseAddresses={warehouseAddresses}
                                    />
                                ))}
                                {addSimilarGroupIndex === groupIndex && (
                                    <Row
                                        headings={headings}
                                        material={{ ...material, ...watch() }}
                                        control={control}
                                        key={`add-similar-${groupIndex}`}
                                        isEdit={false}
                                        isAddSimilar
                                        onEditConfirm={() => {
                                            const isUnique = isUniqueEntry(getValues(), fields);
                                            if (isUnique) {
                                                setError('');
                                                let mappedValues = removeNullFields(getValues());
                                                mappedValues = mapInputs(mappedValues);
                                                append(mappedValues);
                                                cancelEditRow();
                                            } else {
                                                setError(t('application.addRfq.itemExists'));
                                            }
                                        }}
                                        onEditCancel={() => {
                                            setError('');
                                            cancelEditRow();
                                        }}
                                        errors={errors}
                                        notificationError={error}
                                        isValid={isValid}
                                        warehouseAddresses={warehouseAddresses}
                                    />
                                )}
                            </FormProvider>
                        </tbody>
                    </table>
                </div>
            </Card>
        </section>
    );
};
